// @mui
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format, isAfter, isEqual } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { visitApi } from 'src/api/visit';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import ServiceBadge from 'src/components/service-badge';
import VisitPaymentStatusBadge from 'src/components/visit-payment-status-badge/VisitPaymentStatusBadge';
import VisitStatusBadge from 'src/components/visit-status-badge/VisitStatusBadge';
import VisitTypeBadge from 'src/components/visit-type-badge/VisitTypeBadge';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { DARK_TEXT } from 'src/theme/palette';
import {
  GetVisitById,
  GetVisitByIdStatusEnum,
  GetVisitByIdVisitTypeEnum,
  PaymentStatusEnum,
} from 'woofwoof-api';
import DocumentCarousel from '../../visit/components/DocumentCarousel';
import { upcomingVisitHeadingKey } from '../../visit/utils/upcomingVisitHeadingKey';
import OrganizationUpcomingVisitCardTabs from './OrganizationUpcomingVisitCardTabs';
import { translateKey } from 'src/locales/translate';


// ---------------------------------------------------------------------

type OrganizationUpcomingVisitCardProps = {
  visit?: GetVisitById;
  onEdit?: (visitId: string) => void;
  onCancel?: (visitId: string) => void;
  onNotesSaved?: () => void;
  isPatientContext?: boolean;
  isProfessionalContext?: boolean;
};

// TODO: Add proper type for transaction status
export const paymentStatus = {
  [PaymentStatusEnum.Verified]: translateKey("visit.paymentPaid"),
  [PaymentStatusEnum.Created]: translateKey("visit.calendarFilterStatusPending"),
  [PaymentStatusEnum.Failed]: translateKey("visit.calendarFilterStatusRejected"),
  [PaymentStatusEnum.Refunded]: translateKey("visit.paymentRefunded"),
};

export const NUMBER_OF_HOURS_BEFORE_LOCK = 6;

export const isTimeEqualOrPast = (inputTime: string): boolean => {
  const now = new Date();
  const visitTime = new Date(inputTime);

  return isEqual(now, visitTime) || isAfter(now, visitTime);
};

export default function OrganizationUpcomingVisitCard({
  visit,
  onEdit,
  onCancel,
  onNotesSaved,
  isPatientContext,
  isProfessionalContext,
}: OrganizationUpcomingVisitCardProps) {
  const theme = useTheme();
  const isDown500Px = useMediaQuery((theme: any) =>
    theme.breakpoints.down(500),
  );
  const [activeTab, setActiveTab] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [internalNotes, setInternalNotes] = useState(
    (visit as any)?.internalNotes || '',
  );
  const isInternalNotesTab = activeTab === 2;

  const handleCancelEdit = useCallback(() => {
    setInternalNotes((visit as any)?.internalNotes || '');
    setIsEditing(false);
  }, [visit]);

  const handleSaveNotes = useCallback(async () => {
    if (!visit) return;
    try {
      setIsSubmitting(true);
      await visitApi.visitControllerAddInternalNotes({
        visitId: visit.id,
        addInternalNotesDto: { internalNotes },
      });
      (visit as any).internalNotes = internalNotes;
      setIsEditing(false);
      onNotesSaved?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [visit, internalNotes, onNotesSaved]);

  if (!visit) {
    return null;
  }

  const isVisitStatusPending = visit.status === GetVisitByIdStatusEnum.Pending;
  const isPaymentPending =
    visit.payment?.status === PaymentStatusEnum.Created;

  return (
    <Box
      sx={{
        borderRadius: 2,
        background: theme.palette.background.default,
        boxShadow: theme.customShadows.z8,
        marginTop: { xs: 1, sm: 1, md: 1, lg: 0, xl: 0 },
      }}
    >
      <Grid container>
        <Grid item xs={12} p={2} pb={1.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Stack alignItems="flex-start" gap={1}>
              <Typography
                variant="h5"
                sx={{ color: theme.palette.text.secondary }}
              >
                {translateKey(upcomingVisitHeadingKey(visit.status))}{' '}
                <Box
                  component="span"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {format(new Date(visit.startTime), 'd MMMM', {
                    locale: pl,
                  })}
                </Box>{' '}{translateKey("visit.atTimePrefix")}{' '}
                <Box
                  component="span"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {format(new Date(visit.startTime), 'H:mm', {
                    locale: pl,
                  })}
                </Box>
              </Typography>
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <VisitTypeBadge visitType={visit.visitType} />
                {visit.visitAddress && (
                  <Label
                    variant="soft"
                    color="success"
                    sx={{
                      textTransform: 'none',
                      width: 'fit-content',
                    }}
                  >
                    <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                      <Typography
                        sx={{ color: 'inherit', font: 'inherit' }}
                        component="span"
                      >{translateKey("visit.visitAddressLabelColon")}</Typography>
                      <Link
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          `${visit.visitAddress?.street}, ${visit.visitAddress?.zipCode} ${visit.visitAddress?.city}`,
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: 'underline',
                          color: 'inherit',
                        }}
                      >
                        {`${visit.visitAddress?.street}, ${visit.visitAddress?.zipCode} ${visit.visitAddress?.city}`}
                      </Link>
                    </Stack>
                  </Label>
                )}
                {visit?.service && (
                  <ServiceBadge
                    serviceName={visit.service.name}
                    duration={visit.service.duration}
                    price={visit.service.price}
                    visitType={visit.visitType}
                    showPrice
                  />
                )}
                {visit.visitType === GetVisitByIdVisitTypeEnum.Onsite ? (
                  <VisitStatusBadge
                    visitStatus={visit.status}
                    rejectedBy={visit.rejectedBy}
                  />
                ) : (
                  <VisitPaymentStatusBadge
                    paymentStatus={visit.payment?.status}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ borderStyle: 'dashed' }} />
        </Grid>
        <Grid item xs={12} p={2} py={1.5}>
          <Stack
            sx={{
              gap: 2,
            }}
          >
            {isPatientContext && (
              <Stack sx={{ gap: 1 }}>
                <Typography>{translateKey("search.book.cardSpecialist")}</Typography>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Avatar
                    src={
                      visit?.professional?.avatar ||
                      '/assets/avatars/avatar.png'
                    }
                    sx={{ width: '32px', height: '32px' }}
                  />
                  <Stack>
                    <Link
                      component={RouterLink}
                      to={PATH_DASHBOARD.general.professionals.calendar(
                        visit?.professional?.id || '',
                      )}
                      color="text.secondary"
                      underline="always"
                      variant="body1"
                    >
                      {`${visit?.professional?.firstName} ${visit?.professional?.lastName}`}
                    </Link>
                  </Stack>
                </Stack>
              </Stack>
            )}
            {isProfessionalContext && (
              <Stack sx={{ gap: 1 }}>
                <Typography>Pacjent</Typography>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Avatar
                    src={
                      visit?.profile?.avatar || '/assets/avatars/avatar4.png'
                    }
                    sx={{ width: '32px', height: '32px' }}
                  />
                  <Stack>
                    {visit?.profile?.id ? (
                      <Link
                        component={RouterLink}
                        to={PATH_DASHBOARD.general.patients.history(
                          visit.profile.id,
                        )}
                        color="text.secondary"
                        underline="always"
                        variant="body1"
                      >
                        {visit.profile.name}
                      </Link>
                    ) : (
                      <Typography color="text.secondary" variant="body1">
                        {visit?.profile?.name}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ borderStyle: 'dashed' }} />
        </Grid>
        {isPatientContext || isProfessionalContext ? (
          <Grid item xs={12} px={2} pb={1.5}>
            <OrganizationUpcomingVisitCardTabs
              visit={visit}
              isEditing={isEditing}
              internalNotes={internalNotes}
              onInternalNotesChange={setInternalNotes}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </Grid>
        ) : (
          <Grid item xs={12} p={2} py={1.5}>
            <Stack sx={{ gap: 2 }}>
              <Stack sx={{ gap: 1 }}>
                <Typography>{translateKey("visit.complaintsHeading")}</Typography>
                <Typography color="text.secondary">
                  {visit.description}
                </Typography>
              </Stack>
              {!!visit.documents.length && (
                <Stack sx={{ gap: 1 }}>
                  <Typography>{translateKey("quickBooking.attachments")}</Typography>
                  <DocumentCarousel
                    documents={visit.documents as any}
                    visitId={visit.id}
                  />
                </Stack>
              )}
            </Stack>
          </Grid>
        )}
        {!isVisitStatusPending && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Grid>
            <Grid item xs={12} p={2} pt={1.5}>
              <Stack
                sx={{
                  flexDirection: isDown500Px ? 'column' : 'row',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                {/* Notes buttons — left side, visible only on internal notes tab */}
                <Stack direction="row" gap={2}>
                  {(isPatientContext || isProfessionalContext) &&
                    isInternalNotesTab &&
                    isEditing && (
                      <>
                        <Button
                          variant="outlined"
                          color="inherit"
                          size={isDown500Px ? 'large' : 'medium'}
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                        >{translateKey("common.cancel")}</Button>
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          size={isDown500Px ? 'large' : 'medium'}
                          onClick={handleSaveNotes}
                          loading={isSubmitting}
                        >{translateKey("visit.saveInternalNotes")}</LoadingButton>
                      </>
                    )}
                  {(isPatientContext || isProfessionalContext) &&
                    isInternalNotesTab &&
                    !isEditing && (
                      <Button
                        variant="contained"
                        size={isDown500Px ? 'large' : 'medium'}
                        sx={{
                          background: theme.palette.common.black,
                          color: DARK_TEXT.primary,
                        }}
                        startIcon={<Iconify icon="ic:edit" />}
                        onClick={() => setIsEditing(true)}
                      >{translateKey("visit.editInternalNotes")}</Button>
                    )}
                </Stack>
                {/* Visit action buttons — right side */}
                <Stack direction="row" gap={2} justifyContent="flex-end">
                  {onCancel && (
                    <Button
                      variant="outlined"
                      color="error"
                      size={isDown500Px ? 'large' : 'medium'}
                      startIcon={<Iconify icon="ic:close" />}
                      onClick={() => onCancel(visit.id)}
                      disabled={isPaymentPending}
                    >{translateKey("visit.cancelVisitShort")}</Button>
                  )}
                  {onEdit && (
                    <Button
                      variant="contained"
                      size={isDown500Px ? 'large' : 'medium'}
                      sx={{
                        background: theme.palette.common.black,
                        color: DARK_TEXT.primary,
                      }}
                      startIcon={<Iconify icon="mdi:calendar-edit" />}
                      onClick={() => onEdit(visit.id)}
                      disabled={isPaymentPending}
                    >{translateKey("visit.bookingBreadcrumbReschedule")}</Button>
                  )}
                </Stack>
              </Stack>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
}
